import React from 'react';

const PrintableReport = ({ employee: emp, entries, month, companyAddress, LOGO_URL, t, formatHours, stats }) => {
    const params = { stats }; // Create params object for compatibility with the previous paste
    const totalHours = entries.reduce((sum, e) => sum + (Number(e.duration) || 0), 0);
    const sortedEntries = [...entries].sort((a, b) => new Date(a.date) - new Date(b.date));

    // Increase max entries to tightly fit a full month (up to 32 days) on a single page
    const MAX_ENTRIES_PER_PAGE = 35;
    const pages = [];
    for (let i = 0; i < sortedEntries.length; i += MAX_ENTRIES_PER_PAGE) {
        pages.push(sortedEntries.slice(i, i + MAX_ENTRIES_PER_PAGE));
    }
    if (pages.length === 0) pages.push([]);

    return (
        <div className="printable-report-container w-full bg-white">
            {pages.map((pageEntries, pageIndex) => (
                <div key={pageIndex} className="pdf-page h-full flex flex-col bg-white text-black text-xs font-sans print:break-after-page" style={{ pageBreakAfter: 'always', minHeight: '270mm' }}>
                    {/* HEADER */}
            <div className="flex justify-between items-start border-b border-black pb-1 mb-2 shrink-0">
                <div>
                    <p className="text-[9px] text-gray-500 whitespace-pre-line">{companyAddress}</p>
                </div>
                <div className="text-right">
                    <img src={LOGO_URL} alt="Logo" className="h-8 object-contain ml-auto mb-1" />
                    <h1 className="text-base font-bold text-black">{t('printTitle')}</h1>
                    <p className="text-[9px] text-gray-600">{t('monthlyStatus')} {month}</p>

                </div>
            </div>

            {/* EMPLOYEE INFO */}
            <div className="mb-2 bg-gray-50 p-1.5 rounded border border-gray-200 shrink-0">
                <p className="text-[9px] text-gray-500 uppercase font-bold">{t('employee')}</p>
                <p className="text-sm font-bold text-black">{emp.name}</p>
            </div>

            {/* ENTRIES TABLE - Flex Grow to fill space, but overflow hidden to enforce page limit */}
            <div className="flex-grow overflow-hidden border-t border-black">
                <div className="grid grid-cols-5 gap-2 p-1 font-bold text-[9px] text-black border-b border-black bg-gray-100 print:bg-transparent">
                    <div>{t('dateLabel')}</div>
                    <div>{t('startLabel')}</div>
                    <div>{t('endLabel')}</div>
                    <div className="text-right">{t('breaks') || 'Pause'}</div>
                    <div className="text-right">{t('duration')}</div>
                </div>
                <div className="overflow-hidden">
                    {pageEntries.map((entry, idx) => (
                        <div key={idx} className={`grid grid-cols-5 gap-2 p-0.5 border-b border-gray-200 text-[9px] print:border-gray-300 ${entry.type === 'SICK' ? 'text-red-600 bg-red-50 print:bg-transparent' : entry.type === 'VACATION' ? 'text-yellow-600 bg-yellow-50 print:bg-transparent' : ''}`}>
                            <div>{new Date(entry.date).toLocaleDateString('de-DE')}</div>
                            {entry.type === 'SICK' ? (
                                <div className="col-span-2 font-bold uppercase">🤒 {t('sick') || 'Krankheit'}</div>
                            ) : entry.type === 'VACATION' ? (
                                <div className="col-span-2 font-bold uppercase">🏖️ {t('vacation') || 'Urlaub'}</div>
                            ) : (
                                <>
                                    <div>{entry.startTime}</div>
                                    <div>{entry.endTime}</div>
                                </>
                            )}
                            <div className="text-right font-mono text-gray-600">
                                {entry.type === 'SICK' || entry.type === 'VACATION' ? '-' : (entry.breakDuration ? formatHours(entry.breakDuration / 60) : '-')}
                            </div>
                            <div className="text-right font-mono font-bold">{formatHours(entry.duration)}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* SUMMARY & SIGNATURES - Pushed to bottom */}
            <div className="mt-auto shrink-0 pt-4 pb-2">
                {pageIndex === pages.length - 1 && (
                <>
                <div className="border-t-2 border-black pt-1.5 mb-16">
                    <div className="flex justify-between items-start">
                        <h3 className="font-bold text-sm uppercase">{t('summary')}</h3>
                        <div className="text-right space-y-1">
                            {/* Carryover from Previous Month */}
                            <div className="flex justify-end gap-4 text-[10px]">
                                <span className="text-gray-600">Übertrag (Vormonat):</span>
                                <span className={`font-mono font-bold ${params?.stats?.carryOver < 0 ? 'text-red-600' : ''}`}>
                                    {formatHours(params?.stats?.carryOver || 0)}
                                </span>
                            </div>

                            {/* Worked Hours */}
                            <div className="flex justify-end gap-4 text-[10px]">
                                <span className="text-gray-600">Gearbeitet:</span>
                                <span className="font-mono font-bold">
                                    {formatHours(params?.stats?.worked || totalHours)}
                                </span>
                            </div>

                            {/* Paid / Target Hours */}
                            {params?.stats?.hasPaidEntry && (
                                <div className="flex justify-end gap-4 text-[10px]">
                                    <span className="text-gray-600">Abrechnung (Manuell):</span>
                                    <span className="font-mono font-bold">
                                        - {formatHours(params?.stats?.paidOrDue)}
                                    </span>
                                </div>
                            )}

                            {/* Final Balance / New Carryover */}
                            <div className="flex justify-end gap-4 text-xs mt-1 pt-1 border-t border-gray-400">
                                <span className="font-bold">Neuer Übertrag:</span>
                                <span className={`font-mono font-bold ${(params?.stats?.balance) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    {formatHours(params?.stats?.balance || 0)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-between text-xs pb-2">
                    <div className="text-center w-1/3">
                        <p className="border-t border-black pt-1">{t('signatureEmployee')}</p>
                    </div>
                    <div className="text-center w-1/3">
                        <p className="border-t border-black pt-1">{t('signatureEmployer')}</p>
                    </div>
                </div>
                </>
                )}
            </div>
        </div>
        ))}
        </div>
    );
};

export default PrintableReport;
